import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { Firestore, doc, updateDoc } from '@angular/fire/firestore';
import { environment } from '../../../../../environments/environment';
import { ThemeService } from '../../../../core/services/theme.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    RouterModule,
  ],
  template: `
    <div class="login-container">
      <mat-card class="login-card">
        <div class="login-card-content">
          <div class="logo-side">
            <img
              src="assets/engineer-insight-logo.png"
              alt="Engineers Insight Logo"
              class="login-logo"
            />
          </div>
          <div class="form-side">
            <h1 class="login-title">Login</h1>
            <form
              [formGroup]="loginForm"
              (ngSubmit)="onSubmit()"
              [attr.data-testid]="'login-form'"
            >
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Email</mat-label>
                <input
                  matInput
                  formControlName="email"
                  type="email"
                  [attr.data-testid]="'email-input'"
                />
                @if (loginForm.get('email')?.hasError('required')) {
                  <mat-error [attr.data-testid]="'email-required-error'"
                    >Email is required</mat-error
                  >
                }
                @if (loginForm.get('email')?.hasError('email')) {
                  <mat-error [attr.data-testid]="'email-invalid-error'"
                    >Please enter a valid email</mat-error
                  >
                }
              </mat-form-field>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Password</mat-label>
                <input
                  matInput
                  formControlName="password"
                  type="password"
                  [attr.data-testid]="'password-input'"
                />
                @if (loginForm.get('password')?.hasError('required')) {
                  <mat-error [attr.data-testid]="'password-required-error'"
                    >Password is required</mat-error
                  >
                }
              </mat-form-field>
              <button
                mat-flat-button
                color="primary"
                class="full-width"
                type="submit"
                [attr.data-testid]="'login-button'"
                [disabled]="loading || loginForm.invalid"
              >
                @if (loading) {
                  <mat-spinner diameter="20"></mat-spinner>
                }
                @if (!loading) {
                  <span>Login</span>
                }
              </button>
            </form>
            @if (showDebugInfo) {
              <div class="debug-link">
                <a [routerLink]="['/debug']">Debug Auth Status</a>
              </div>
            }
          </div>
        </div>
      </mat-card>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .login-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100vh;
        width: 100%;
        background-color: var(--color-bg);
      }

      .login-card {
        width: 800px;
        max-width: 90%;
        padding: 0;
        overflow: hidden;
        border-radius: var(--color-radius);
        background-color: var(--color-bg-card);
        border: 1px solid var(--color-border);
        box-shadow: none;
      }

      .login-card-content {
        display: flex;
        flex-direction: row;
        min-height: 400px;
      }

      .logo-side {
        flex: 1;
        background-color: #0f0f0f;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 2rem;
      }

      .login-logo {
        max-width: 90%;
        max-height: 80%;
      }

      .form-side {
        flex: 1;
        padding: 2rem;
        display: flex;
        flex-direction: column;
        justify-content: center;
      }

      .login-title {
        font-size: 2rem;
        margin-bottom: 2rem;
        text-align: center;
        color: var(--color-fg);
      }

      .full-width {
        width: 100%;
        margin-bottom: 16px;
      }

      .debug-link {
        text-align: center;
        margin-top: 16px;
        font-size: 0.9em;
      }

      .debug-link a {
        color: var(--color-accent);
        text-decoration: none;
      }

      .debug-link a:hover {
        text-decoration: underline;
      }

      /* Responsive adjustments */
      @media (max-width: 768px) {
        .login-card-content {
          flex-direction: column;
        }

        .logo-side {
          padding: 1.5rem;
        }

        .form-side {
          padding: 1.5rem;
        }
      }
    `,
  ],
})
export class LoginComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private firestore = inject(Firestore);
  private themeService = inject(ThemeService);
  private readonly usersCollection = `environments/${environment.environmentName}/users`;

  loginForm!: FormGroup;
  loading = false;
  showDebugInfo = environment.showDebugInfo;

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    const { email, password } = this.loginForm.value;

    try {
      // Use the async signIn method
      const userCredential = await this.authService.signIn(email, password);

      // Get user info
      const user = userCredential.user;
      if (this.showDebugInfo) {
        console.log('Login successful for user:', user.email);
      }

      // Update last login time in Firestore
      try {
        const userDocRef = doc(
          this.firestore,
          `${this.usersCollection}/${user.uid}`,
        );
        await updateDoc(userDocRef, {
          lastLogin: new Date(),
        });
        if (this.showDebugInfo) {
          console.log('Updated last login time in Firestore');
        }
      } catch (error) {
        console.error('Error updating last login time:', error);
        // Continue with login even if this fails
      }

      // Force token refresh to ensure we have the latest claims
      await user.getIdToken(true);

      // Redirect to the stored URL or default to dashboard
      const redirectUrl = localStorage.getItem('redirectUrl') || '/dashboard';
      if (this.showDebugInfo) {
        console.log('Redirecting to:', redirectUrl);
      }
      this.router.navigate([redirectUrl]);
    } catch (error) {
      this.loading = false;
      this.snackBar.open(
        'Login failed. Please check your credentials.',
        'Close',
        { duration: 3000 },
      );
      console.error('Login error details:', error);
    }
  }
}
